import {Injectable, NgZone} from "@angular/core";
import posthog from "posthog-js";
import {environment} from "../../environments/environment";
import {UserProfile} from "../shared/types/UserProfile";
import {StateService} from './state.service';
import {NavigationEnd, Router} from '@angular/router';
import {filter} from 'rxjs';
import {PosthogEventName, PosthogEventCode} from './types';

@Injectable({providedIn: "root"})
export class PosthogService {
  constructor(private ngZone: NgZone, private stateService: StateService, private router: Router) {
    this.initPostHog();
    this.trackPageViews();
  }

  private initPostHog(): void {
    if (this.isPosthogDisabled()) {
      return;
    }
    
    this.ngZone.runOutsideAngular(() => {
      posthog.init(environment.posthogKey, {
        api_host: environment.posthogApiHost,
        ui_host: environment.posthogHost,
        defaults: '2025-05-24',
        capture_pageview: false,
      });
    });
  }

  private trackPageViews(): void {
    if (this.isPosthogDisabled()) {
      return;
    }
    
    this.ngZone.runOutsideAngular(() => {
      this.router.events
        .pipe(filter(event => event instanceof NavigationEnd))
        .subscribe((event: NavigationEnd) => {
          posthog.capture("$pageview", {
            $current_url: window.location.href,
            $pathname: event.urlAfterRedirects,
            viewed_account: this.stateService.activeAccount(),
          });
        });
    });
  }

  public identify(user: UserProfile): void {
    if (this.isPosthogDisabled()) {
      return;
    }
    
    this.ngZone.runOutsideAngular(() => {
      posthog.identify(user.id, {
        email: user.email,
        name: `${user.firstName} ${user.lastName}`,
        role: user.role || "NA",
        account: user.account || "NA",
        subscriptionStatus: user.subscriptionStatus || "NA",
      });
    });
  }

  public reset(): void {
    if (this.isPosthogDisabled()) {
      return;
    }
    
    this.ngZone.runOutsideAngular(() => {
      posthog.reset();
    });
  }

  public capture(eventName: string, properties: Record<string, any> = {}): void {
    if (this.isPosthogDisabled()) {
      return;
    }
    
    this.ngZone.runOutsideAngular(() => {
      posthog.capture(eventName, properties);
    });
  }

  public captureVolumeFilter(widgetName: string, threshold: number | null): void {
    if (this.isPosthogDisabled()) {
      return;
    }
    
    this.capture(PosthogEventName.WIDGET_VOLUME_FILTER_APPLIED, {
      [PosthogEventCode.VOLUME_THRESHOLD]: threshold,
      [PosthogEventCode.WIDGET_NAME]: widgetName
    });
  }

  private isPosthogDisabled(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }
    const hostname = window.location.hostname;
    return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]';
  }

}
